/**
 * The sync wire protocol — the one module imported by *both* the browser and
 * the Durable Object.
 *
 * Every frame is JSON over a single WebSocket. There is no HTTP fallback: the
 * socket carries pushes, pulls, acks, deltas and snapshots, and closing it is
 * the offline signal.
 *
 * ## Client → server
 *
 * | Frame | Meaning |
 * | --- | --- |
 * | `push` | "apply these commands; I have seen up to `lastSeenVersion`" |
 * | `pull` | "send me everything after `sinceVersion`" |
 * | `presence` | "I am on this section and row now" (rooms only; see below) |
 *
 * `lastSeenVersion` on a push is load-bearing, not diagnostic. The server sends
 * the pusher any commands it is missing **before** the `ack`, so the client can
 * fold its own accepted commands onto a server prefix it has actually seen.
 * Without it, a client whose push interleaves with another device's would apply
 * its own command at a version whose predecessor it never received.
 *
 * ## Server → client
 *
 * | Frame | Meaning |
 * | --- | --- |
 * | `ack` | per-command outcome for one push, plus the version before and after |
 * | `commands` | the contiguous log slice `fromVersion+1 … toVersion` |
 * | `snapshot` | the whole workspace at `version`; sent when a delta is impossible |
 * | `error` | protocol-level failure; the socket may or may not stay open |
 * | `presence` | who is in a shared recipe's room, and where (rooms only) |
 *
 * ## Presence
 *
 * A shared recipe's room (PLAN-COLLAB D-CO12) carries one more pair of frames
 * on the same socket: a member says which section and row of the recipe they
 * are on, and the room answers everybody with the whole roster. Presence is
 * not part of the log. It consumes no version, it is never stored, and it
 * lives only in the socket's hibernation attachment, so it ends with the
 * socket. A workspace has one person in it and nobody to tell, so the
 * workspace object answers a `presence` frame with `bad-message`.
 *
 * ## Versions
 *
 * A version is a positive integer assigned by the server, and **only accepted
 * commands consume one**. A rejected command leaves the version untouched, so
 * the log is gap-free by construction and `commands` frames can be validated
 * for contiguity on arrival.
 *
 * Version `0` means "empty workspace, nothing applied yet" and is the correct
 * `sinceVersion` for a client that has never synced.
 *
 * ## Compatibility
 *
 * {@link SYNC_PROTOCOL_VERSION} is sent as a query parameter on the socket
 * upgrade. The server refuses a version it does not implement rather than
 * guessing, which is cheaper than making every frame self-describing.
 */

import type { Command, CommandError, CommandErrorCode, WorkspaceState } from '@beerwright/commands';
import { commandSchema, documentIdSchema, workspaceStateSchema } from '@beerwright/commands';
import { z } from 'zod';

/**
 * Bumped when a frame's meaning changes incompatibly. Sent as `?v=` on the
 * upgrade, and refused at the Worker's door when it does not match.
 *
 * **4** — a shared recipe's room (PLAN-COLLAB). Both unions gained a `presence`
 * frame (D-CO12), which a bundle from before the bump decodes as unreadable, and
 * `commandErrorSchema` gained `recipe-shared`, which the strict enum of a bundle
 * from before the bump refuses, taking the whole `ack` down with it. The code
 * shipped on the branch first without a bump; one bump covers both, because the
 * branch deploys as one unit (ruling R3).
 *
 * **3** — a blend's cellar tasks (PLAN-UX-PASS E1). `cellarTasks` is a required
 * key on `blendDocFieldsSchema`, which is a `z.strictObject` that
 * `workspaceStateSchema` parses every blend through. So a bundle from before the
 * bump reads a `snapshot` frame for a workspace with blends as invalid, and
 * refuses every pushed `blend.create` `from` and `blend.restore` payload that
 * carries the key. Four earlier blend fields (`stage`, `stageDates`, `readings`
 * and `additions`, 2026-09-19 and -20) shipped without a bump, which was wrong
 * by the same rule: the gate covers document fields a strict schema refuses, not
 * only command payloads, so this bump covers those four as well.
 *
 * **2** — ingredient links (PLAN-INGREDIENT-LINKS). Lots and recipe rows gained
 * `ingredientId`, so `inventory.add`, the four `recipe.add*` verbs and
 * `batch.setInventoryDeducted` now carry a key that a strict payload schema
 * from before the link rejects as `invalid-payload`. A bundle from before the
 * bump cannot apply what a bundle from after it pushes, and the reducer also
 * rewrites linked lots on `customIngredient.update`, so the two would compute
 * different states from one log. The version is the honest gate.
 *
 * **1** — the initial protocol.
 */
export const SYNC_PROTOCOL_VERSION = 4;

/** The WebSocket endpoint, relative to the origin. */
export const SYNC_PATH = '/api/sync';

/**
 * The liveness probe, and the only two frames that are not JSON.
 *
 * They are a literal pair because workerd answers them for us:
 * `ctx.setWebSocketAutoResponse(new WebSocketRequestResponsePair(PING_FRAME,
 * PONG_FRAME))` replies from the runtime without waking a hibernating object,
 * which is what makes a heartbeat affordable at all. Both ends therefore have
 * to agree on the exact strings, so they live here beside the frames they are
 * deliberately not part of: neither decoder ever sees one.
 */
export const PING_FRAME = 'ping';
/** @see PING_FRAME */
export const PONG_FRAME = 'pong';

/** Largest number of commands one `push` may carry. */
export const MAX_PUSH_COMMANDS = 200;

/** Largest number of commands one `commands` frame may carry. Also the delta page size. */
export const MAX_DELTA_COMMANDS = 500;

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

/** A server-assigned log position. */
const versionSchema = z.number().int().min(0);

/**
 * The reducer's error, on the wire.
 *
 * Structurally the `CommandError` from `@beerwright/commands`, restated here
 * because that package exports the type but not a schema for it — and a frame
 * arriving from the network gets validated like everything else.
 */
export const commandErrorSchema: z.ZodType<CommandError> = z.strictObject({
  code: z.enum([
    'unknown-command',
    'malformed-envelope',
    'invalid-payload',
    'not-found',
    'conflict',
    'invalid-state',
    // A removal something still points at; see the commands package's CommandErrorCode.
    'in-use',
    // Server-only: produced by the Durable Object when a create would pass the
    // account's recipe cap (PLAN §9). The reducer never emits it.
    'plan-limit',
    // Server-only: the workspace Durable Object passed the command on to the
    // room its recipe now lives in (PLAN-COLLAB B4). Settled, not failed.
    'recipe-shared',
  ]),
  message: z.string(),
  commandId: z.string().nullable(),
  commandType: z.string().nullable(),
  issues: z.array(z.strictObject({ path: z.string(), message: z.string() })),
  index: z.number().int().nullable(),
});

/**
 * What became of one pushed command.
 *
 * - `applied` — accepted; `version` is the position it took.
 * - `duplicate` — this `commandId` was already in the log; `version` is where it
 *   sits. The client prunes it exactly as if it had just applied, which is what
 *   makes an at-least-once outbox safe.
 * - `rejected` — the reducer refused it, or the server could not parse it
 *   (`unknown-command`, `malformed-envelope`, `invalid-payload`); `error` says
 *   why and the server state is unchanged **for that command only**. The client
 *   drops it from the outbox and reconciles without it.
 */
export const commandResultSchema = z.strictObject({
  commandId: z.string(),
  status: z.enum(['applied', 'duplicate', 'rejected']),
  version: versionSchema.nullable(),
  error: commandErrorSchema.nullable(),
});

/** @see commandResultSchema */
export type CommandResult = z.infer<typeof commandResultSchema>;

/**
 * The rejection codes that mean a build is wrong, not that two devices raced.
 *
 * A command the server cannot *read* was either sent by a client this server
 * has never heard of, or built wrong by a client that should know better —
 * either way somebody shipped a skew, and nobody is watching a console to find
 * out. `not-found`, `conflict`, `in-use` and `invalid-state` are the opposite:
 * ordinary outcomes of two devices editing the same workspace, and reporting
 * those would bury the real ones.
 */
export const PARSE_ERROR_CODES = [
  'unknown-command',
  'malformed-envelope',
  'invalid-payload',
] as const satisfies readonly CommandErrorCode[];

/** Whether a rejection is one of {@link PARSE_ERROR_CODES}. */
export function isParseError(code: CommandErrorCode): boolean {
  return (PARSE_ERROR_CODES as readonly CommandErrorCode[]).includes(code);
}

/** Why the server sent an `error` frame. */
export const syncErrorCodeSchema = z.enum([
  /** No valid session, or the session's user does not own this workspace. */
  'unauthorized',
  /**
   * The frame did not parse, or carried more commands than allowed.
   *
   * A single command the server cannot parse is **not** this: it comes back as
   * a `rejected` result naming that `commandId`, so the client can drop the one
   * bad command instead of re-sending the whole batch forever.
   */
  'bad-message',
  /** {@link SYNC_PROTOCOL_VERSION} mismatch. */
  'unsupported-version',
  /** Storage or reducer failure the server could not attribute to one command. */
  'internal',
]);

/** @see syncErrorCodeSchema */
export type SyncErrorCode = z.infer<typeof syncErrorCodeSchema>;

/**
 * The sections of the recipe page a member can be on, in page order.
 *
 * A closed list, validated at the room, so that the only thing a presence
 * frame can say about somebody is a place the page actually has.
 */
export const PRESENCE_SECTIONS = [
  'overview',
  'fermentables',
  'hops',
  'yeasts',
  'miscs',
  'mash',
  'fermentation',
  'water',
  'notes',
] as const;

/** @see PRESENCE_SECTIONS */
export type PresenceSection = (typeof PRESENCE_SECTIONS)[number];

/**
 * Where one member is: a section, and the row inside it when they are on one.
 * `null` for a member who is in the room but on no section at all.
 *
 * `rowId` is a document id (an ingredient row's `itemId`, a mash step's id),
 * held to the same pattern as every other id, so it cannot carry free text.
 */
export const presenceAtSchema = z
  .strictObject({
    section: z.enum(PRESENCE_SECTIONS),
    rowId: documentIdSchema.nullable(),
  })
  .nullable();

/** @see presenceAtSchema */
export type PresenceAt = z.infer<typeof presenceAtSchema>;

// ---------------------------------------------------------------------------
// Client → server
// ---------------------------------------------------------------------------

/**
 * What a push carries per slot: enough to name the command in an ack.
 *
 * Deliberately looser than {@link commandSchema}. The Durable Object parses
 * each slot itself, so a command the server's schema refuses is answered as a
 * `rejected` result for that one command rather than as a `bad-message` for the
 * whole frame — which is what a client can act on. A frame validated here in
 * full would leave the client re-sending the same doomed batch on every
 * reconnect, with nothing said about which command was at fault.
 *
 * The `commandId` is the part the server genuinely needs: without it there is
 * nothing to put in the ack, and the frame really is unanswerable.
 */
export const pushedCommandSchema: z.ZodType<PushedCommand> = z.looseObject({
  commandId: z.string().min(1).max(64),
});

/**
 * @see pushedCommandSchema
 *
 * Written out rather than inferred: the inferred shape carries a string index
 * signature, and a `Command` — an interface — does not, so a real command would
 * not be assignable to it.
 */
export type PushedCommand = { readonly commandId: string };

/** "Apply these, and catch me up first if I am behind." */
export const pushMessageSchema = z.strictObject({
  type: z.literal('push'),
  commands: z.array(pushedCommandSchema).min(1).max(MAX_PUSH_COMMANDS),
  lastSeenVersion: versionSchema,
});

/** "Send me everything after this version." */
export const pullMessageSchema = z.strictObject({
  type: z.literal('pull'),
  sinceVersion: versionSchema,
});

/**
 * "I am here now." Rooms only; see the module's note on presence.
 *
 * The client throttles these. The room trusts nothing else about them: the
 * frame names no user, because the socket already says who sent it.
 */
export const presenceMessageSchema = z.strictObject({
  type: z.literal('presence'),
  at: presenceAtSchema,
});

/** Everything a client may send. */
export const clientMessageSchema = z.discriminatedUnion('type', [
  pushMessageSchema,
  pullMessageSchema,
  presenceMessageSchema,
]);

/** @see clientMessageSchema */
export type ClientMessage = z.infer<typeof clientMessageSchema>;

/** @see pushMessageSchema */
export type PushMessage = z.infer<typeof pushMessageSchema>;

/** @see pullMessageSchema */
export type PullMessage = z.infer<typeof pullMessageSchema>;

/** @see presenceMessageSchema */
export type PresenceMessage = z.infer<typeof presenceMessageSchema>;

// ---------------------------------------------------------------------------
// Server → client
// ---------------------------------------------------------------------------

/**
 * The outcome of one push.
 *
 * `fromVersion` is the log head before the batch and `version` the head after,
 * so a client can assert it was not skipped ahead: `fromVersion` must equal the
 * version it believes the server is at, once any preceding `commands` frame in
 * the same burst has been applied.
 */
export const ackMessageSchema = z.strictObject({
  type: z.literal('ack'),
  results: z.array(commandResultSchema),
  fromVersion: versionSchema,
  version: versionSchema,
});

/**
 * A contiguous slice of the log: exactly versions `fromVersion+1 … toVersion`.
 *
 * `commands.length === toVersion - fromVersion` always holds, which is the
 * client's contiguity check. An empty slice (`fromVersion === toVersion`) is the
 * normal "you are already up to date" reply to a `pull`.
 */
export const commandsMessageSchema = z.strictObject({
  type: z.literal('commands'),
  commands: z.array(commandSchema).max(MAX_DELTA_COMMANDS),
  fromVersion: versionSchema,
  toVersion: versionSchema,
});

/**
 * The whole workspace, when a delta cannot be built.
 *
 * Sent when `sinceVersion` is older than the retained log window, or ahead of
 * the server's head (a client whose workspace was reset server-side). Replaces
 * the client's server prefix wholesale.
 */
export const snapshotMessageSchema = z.strictObject({
  type: z.literal('snapshot'),
  state: workspaceStateSchema,
  version: versionSchema,
});

/** A protocol-level failure. */
export const errorMessageSchema = z.strictObject({
  type: z.literal('error'),
  code: syncErrorCodeSchema,
  message: z.string(),
});

/**
 * One member in the room: who, by the name the room was given for them (a
 * display name or `Co-brewer`, never an address), and where.
 */
export const presencePeerSchema = z.strictObject({
  userId: z.string().min(1),
  name: z.string(),
  at: presenceAtSchema,
});

/** @see presencePeerSchema */
export type PresencePeer = z.infer<typeof presencePeerSchema>;

/**
 * Everybody in the room, one entry per member however many tabs they have
 * open, sent whole every time somebody arrives, moves or leaves. A room seats
 * at most six, so the whole roster is cheaper to reason about than a diff.
 *
 * It includes the member it is sent to; each client leaves itself out.
 */
export const presenceRosterMessageSchema = z.strictObject({
  type: z.literal('presence'),
  peers: z.array(presencePeerSchema),
});

/** Everything a server may send. */
export const serverMessageSchema = z.discriminatedUnion('type', [
  ackMessageSchema,
  commandsMessageSchema,
  snapshotMessageSchema,
  errorMessageSchema,
  presenceRosterMessageSchema,
]);

/** @see serverMessageSchema */
export type ServerMessage = z.infer<typeof serverMessageSchema>;

/** @see ackMessageSchema */
export type AckMessage = z.infer<typeof ackMessageSchema>;

/** @see commandsMessageSchema */
export type CommandsMessage = z.infer<typeof commandsMessageSchema>;

/** @see snapshotMessageSchema */
export type SnapshotMessage = z.infer<typeof snapshotMessageSchema>;

/** @see errorMessageSchema */
export type ErrorMessage = z.infer<typeof errorMessageSchema>;

/** @see presenceRosterMessageSchema */
export type PresenceRosterMessage = z.infer<typeof presenceRosterMessageSchema>;

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/** Serialise a frame. Commands are plain JSON, so this is lossless. */
export function encodeMessage(message: ClientMessage | ServerMessage): string {
  return JSON.stringify(message);
}

/** The result of decoding a frame. */
export type DecodeResult<T> =
  | { readonly ok: true; readonly message: T }
  | { readonly ok: false; readonly reason: string };

function decode<T>(schema: z.ZodType<T>, raw: unknown): DecodeResult<T> {
  if (typeof raw !== 'string') return { ok: false, reason: 'Expected a text frame.' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, reason: 'Frame was not JSON.' };
  }
  const result = schema.safeParse(parsed);
  if (!result.success) {
    return { ok: false, reason: result.error.issues[0]?.message ?? 'Frame failed validation.' };
  }
  return { ok: true, message: result.data };
}

/** Validate a frame from a client. Never throws. */
export function decodeClientMessage(raw: unknown): DecodeResult<ClientMessage> {
  return decode(clientMessageSchema, raw);
}

/** Validate a frame from the server. Never throws. */
export function decodeServerMessage(raw: unknown): DecodeResult<ServerMessage> {
  return decode(serverMessageSchema, raw);
}

// ---------------------------------------------------------------------------
// Constructors
// ---------------------------------------------------------------------------

/** Build a `push`. */
export function pushMessage(commands: readonly Command[], lastSeenVersion: number): PushMessage {
  return { type: 'push', commands: [...commands], lastSeenVersion };
}

/** Build a `pull`. */
export function pullMessage(sinceVersion: number): PullMessage {
  return { type: 'pull', sinceVersion };
}

/** Build a `commands` delta. */
export function commandsMessage(
  commands: readonly Command[],
  fromVersion: number,
  toVersion: number,
): CommandsMessage {
  return { type: 'commands', commands: [...commands], fromVersion, toVersion };
}

/** Build a `snapshot`. */
export function snapshotMessage(state: WorkspaceState, version: number): SnapshotMessage {
  return { type: 'snapshot', state, version };
}

/** Build an `error`. */
export function errorMessage(code: SyncErrorCode, message: string): ErrorMessage {
  return { type: 'error', code, message };
}

/** Build a client's `presence`. */
export function presenceMessage(at: PresenceAt): PresenceMessage {
  return { type: 'presence', at };
}

/** Build the room's `presence` roster. */
export function presenceRosterMessage(peers: readonly PresencePeer[]): PresenceRosterMessage {
  return { type: 'presence', peers: [...peers] };
}
